import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { format, parseISO } from "date-fns"
import { ChevronLeft, ChevronRight, Palette, Plus } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { fetchBookings } from "~/apis/bookings"
import { fetchProperties } from "~/apis/properties"
import { propertyColor, shortName } from "~/lib/calendar"
import { PageSpinner } from "~/components/Spinner"
import routes from "~/routes"

const MONTHS = [ "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec" ]

function dateRange(booking) {
  return `${format(parseISO(booking.starts_on), "EEE d")} → ${format(parseISO(booking.ends_on), "EEE d MMM")}`
}

function ResidencyRow({ residency }) {
  const past = residency.ends_on <= new Date().toLocaleDateString("en-CA")
  const cancelled = residency.status === "cancelled"

  return (
    <li className={cn("rounded-lg border border-border p-3", (past || cancelled) && "opacity-60")}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {MONTHS[parseISO(residency.starts_on).getMonth()]} · {dateRange(residency)} · {residency.nights} nights
            {cancelled && <span className="ml-2 text-destructive">cancelled</span>}
          </p>
          <Link to={routes.bookingDetail(residency.id)} className="mt-0.5 block font-semibold text-foreground hover:underline">
            {residency.guest_name || <span className="font-medium text-amber-600 dark:text-amber-400">Resident TBC — add details</span>}
            {residency.guest_count ? <span className="font-normal text-muted-foreground"> ×{residency.guest_count}</span> : null}
          </Link>
          {(residency.guest_phone || residency.guest_email) && (
            <p className="text-sm text-muted-foreground">
              {[ residency.guest_phone, residency.guest_email ].filter(Boolean).join(" · ")}
            </p>
          )}
          {residency.notes && <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/80">{residency.notes}</p>}
          {residency.clean && (
            <p className="mt-1 text-xs text-muted-foreground">
              Clean: {residency.clean.when_label} — {residency.clean.cleaner_name || "no cleaner yet"}
            </p>
          )}
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to={routes.editBooking(residency.id)}>{residency.guest_name ? "Edit" : "Add details"}</Link>
        </Button>
      </div>
    </li>
  )
}

// One card per property, its residencies for the chosen year in date order.
// A residency is a booking with kind "residency", so everything else (calendar,
// cleans, messages) already knows about it.
export default function Residencies() {
  const [ year, setYear ] = useState(new Date().getFullYear())
  const [ properties, setProperties ] = useState(null)
  const [ residencies, setResidencies ] = useState(null)
  const [ error, setError ] = useState(null)

  useEffect(() => {
    fetchProperties().then(setProperties).catch((err) => setError(err.message))
  }, [])

  useEffect(() => {
    setResidencies(null)
    fetchBookings(`${year}-01-01`, `${year + 1}-01-01`, { kind: "residency" })
      .then(setResidencies)
      .catch((err) => setError(err.message))
  }, [ year ])

  const byProperty = useMemo(() => {
    if (!residencies) return {}
    return residencies.reduce((acc, r) => {
      (acc[r.property_id] = acc[r.property_id] || []).push(r)
      return acc
    }, {})
  }, [ residencies ])

  return (
    <div className="mx-auto max-w-5xl px-4 py-4 md:py-8">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <h1 className="flex items-center gap-2 text-lg font-bold"><Palette size={18} className="text-brand" /> Residencies</h1>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon-sm" onClick={() => setYear(year - 1)} aria-label="Previous year"><ChevronLeft /></Button>
          <span className="w-14 text-center text-sm font-semibold">{year}</span>
          <Button variant="ghost" size="icon-sm" onClick={() => setYear(year + 1)} aria-label="Next year"><ChevronRight /></Button>
        </div>
        <div className="flex-1" />
        <Button asChild size="sm" className="bg-brand text-brand-foreground hover:bg-brand/90">
          <Link to={`${routes.newBooking}?kind=residency`}><Plus /> Residency</Link>
        </Button>
      </div>

      <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
        One stay per property each month. Gardens runs Sunday afternoon to Friday morning, Waters Monday afternoon to Friday morning.
        Residencies sit on the calendar and get cleans like any booking; the cleaner is told automatically when a property's residency note applies.
      </p>

      {error && <p className="mb-3 text-sm text-destructive">{error}</p>}

      {!properties || !residencies ? (
        <PageSpinner />
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {properties.map((property) => {
            const list = (byProperty[property.id] || []).sort((a, b) => a.starts_on.localeCompare(b.starts_on))
            const named = list.filter((r) => r.guest_name && r.status !== "cancelled").length
            return (
              <section key={property.id} className="rounded-xl border border-border bg-card p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="flex items-center gap-2 font-semibold text-foreground">
                    <span className="size-3 rounded-full" style={{ background: propertyColor(property) }} />
                    {shortName(property)}
                  </h2>
                  <span className="text-xs text-muted-foreground">{named} of {list.length} residents confirmed</span>
                </div>
                {property.residency_cleaner_note && (
                  <p className="mb-3 rounded-md bg-muted/60 px-2.5 py-1.5 text-xs text-muted-foreground">Cleaner is told: "{property.residency_cleaner_note}"</p>
                )}
                {list.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No residencies for {year} yet.{" "}
                    <Link to={`${routes.newBooking}?kind=residency&property_id=${property.id}`} className="underline">Add one</Link>
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {list.map((r) => <ResidencyRow key={r.id} residency={r} />)}
                  </ul>
                )}
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}
